import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Stack,
  TextField,
} from "@mui/material";
import { type ReactElement, useMemo, useState } from "react";

import { useToast } from "~hooks/useToast";

import { RoleMultiSelect } from "../../molecules/BulkEmail/RoleMultiSelect";
import { UserMultiSelect } from "../../molecules/BulkEmail/UserMultiSelect";
import { deduplicateBulkEmailUsers } from "./bulkEmailUtils";
import { useBulkEmail } from "./hooks/useBulkEmail";
import { type BulkEmailModalProps, type BulkEmailUser } from "./types";

const tModalTitle = "Send Email";
const tRecipients = "Recipients";
const tFilterByRole = "Filter by Role";
const tSubject = "Subject";
const tMessage = "Message";
const tCancel = "Cancel";
const tSendEmail = "Send Email";
const tSubjectPlaceholder = "Enter email subject";
const tMessagePlaceholder = "Enter your message";
const tErrorSendingEmail = "Failed to send email";
const tSuccessSendingEmail = "Successfully sent email to {count} recipients";

export const BulkEmailModal = ({ isOpen, onClose, contextData, onSend }: BulkEmailModalProps): ReactElement => {
  const toast = useToast();
  const [subject, setSubject] = useState("");
  const [emailContents, setEmailContents] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<BulkEmailUser[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  // De-duplicate upstream data (room/event may return same person multiple times)
  const uniqueAvailableUsers = useMemo(
    () => deduplicateBulkEmailUsers(contextData.availableUsers),
    [contextData.availableUsers],
  );

  // Filter available users based on selected roles, but always include already selected users
  // Then sort alphabetically by name
  const availableUsers = useMemo(() => {
    let filteredUsers;
    if (selectedRoles.length === 0) {
      filteredUsers = uniqueAvailableUsers;
    } else {
      const selectedUserKeys = new Set(selectedUsers.map((user) => user.email || user.id));
      filteredUsers = uniqueAvailableUsers.filter(
        (user) =>
          user.roles.some((role) => selectedRoles.includes(role)) || selectedUserKeys.has(user.email || user.id),
      );
    }

    // Sort alphabetically by name
    return [...filteredUsers].sort((a, b) => a.name.localeCompare(b.name));
  }, [uniqueAvailableUsers, selectedRoles, selectedUsers]);

  // Filter selected users based on roles and sort alphabetically
  const filteredUsers = useMemo(() => {
    let filtered;
    if (selectedRoles.length === 0) {
      filtered = selectedUsers;
    } else {
      filtered = selectedUsers.filter((user) => user.roles.some((role) => selectedRoles.includes(role)));
    }

    // Sort alphabetically by name
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedUsers, selectedRoles]);

  const recipientIds = useMemo(() => Array.from(new Set(filteredUsers.map((user) => user.id))), [filteredUsers]);

  const resetForm = () => {
    setSubject("");
    setEmailContents("");
    setSelectedUsers([]);
    setSelectedRoles([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const { status, sendEmail } = useBulkEmail({
    onError: () => {
      toast.error(tErrorSendingEmail);
    },
    onSuccess: (recipientCount) => {
      toast.success(tSuccessSendingEmail.replace("{count}", String(recipientCount)));
      handleClose();
    },
  });

  const handleSend = async () => {
    try {
      const emailData = {
        bubbleId: contextData.bubbleId,
        emailContents,
        recipients: recipientIds,
        subject,
      };

      await sendEmail(emailData);
      await onSend(emailData);
    } catch (error) {
      // Error is already handled by the hook
      console.warn(error);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>{tModalTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {contextData.availableRoles.length > 0 && (
            <FormControl>
              <FormLabel>{tFilterByRole}</FormLabel>
              <RoleMultiSelect
                availableRoles={contextData.availableRoles}
                selectedRoles={selectedRoles}
                onChange={setSelectedRoles}
                availableUsers={uniqueAvailableUsers}
                onAddUsers={(userIds) => {
                  const usersToAdd = uniqueAvailableUsers.filter((user) => userIds.includes(user.id));
                  const existingKeys = new Set(selectedUsers.map((u) => u.email || u.id));
                  const merged = [
                    ...selectedUsers,
                    ...usersToAdd.filter((u) => !existingKeys.has(u.email || u.id)),
                  ].sort((a, b) => a.name.localeCompare(b.name));

                  setSelectedUsers(merged);
                }}
              />
            </FormControl>
          )}

          <FormControl required>
            <FormLabel>{tRecipients}</FormLabel>
            <UserMultiSelect
              availableUsers={availableUsers}
              selectedUsers={selectedUsers}
              onChange={(users) => {
                const key = (u: BulkEmailUser) => u.email || u.id;
                const uniqueByEmail = users.filter((u, i, arr) => arr.findIndex((x) => key(x) === key(u)) === i);
                const sortedUsers = uniqueByEmail.sort((a, b) => a.name.localeCompare(b.name));
                setSelectedUsers(sortedUsers);
              }}
            />
          </FormControl>

          <FormControl required>
            <FormLabel>{tSubject}</FormLabel>
            <TextField
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={tSubjectPlaceholder}
              fullWidth
            />
          </FormControl>

          <FormControl required>
            <FormLabel>{tMessage}</FormLabel>
            <TextField
              value={emailContents}
              onChange={(e) => setEmailContents(e.target.value)}
              placeholder={tMessagePlaceholder}
              multiline
              rows={8}
              fullWidth
            />
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{tCancel}</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={status === "pending" || !subject || !emailContents || recipientIds.length === 0}
        >
          {tSendEmail}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
